/**
 * Robot control, November 2026 redesign (docs/redesign-2026-11/README.md "Backend contract"): the computer calls the
 * phone (control mode `phone_call`), device flags for calls, the linked standby between tasks (P1) and the single-run
 * read. Hand-written next to the generated platform types, because the services ship these in parallel.
 */
import type { ApiClient } from './client.ts'
import type * as T from './types.ts'

const enc = encodeURIComponent

// ── Calls (platform) ────────────────────────────────────────────────────────────────────────────────────────────
/** A reward week as the server names it (rt_common.weeks): its number, bounds and payout time. */
export interface RewardWeekRef {
  week: number
  starts_at: string
  ends_at: string
  pays_at: string
}

export interface CallCreate {
  /** The sim pre-pairing's token, sealed by the platform and handed to the phone only when it answers. */
  pairing_token?: string
  pairing_id?: string
  task_id?: string
  task_title: string
  task_image?: string | null
  /** A phone of the person's; default: their default phone with `answer_calls`. */
  device_id?: string
  /** Test call (setup, Home): no pairing token; answering returns `{ok: true}`. */
  test?: boolean
}

export interface CallCreated {
  id: string
  /** The two-digit number the phone must tap (shown only on the computer). */
  number: string
  expires_at: string
  device: { id: string; name: string | null }
  state?: CallState
}

export type CallState = 'ringing' | 'answered' | 'declined' | 'wrong' | 'expired'

export interface CallStatus {
  id: string
  state: CallState
  expires_at: string
}

/** PATCH /v1/me/devices/{id}. From the web, `answer_calls: true` waits for the phone to accept it. */
export interface DevicePatch {
  answer_calls?: boolean
  default_controller?: boolean
}

/** A change the phone must accept (turning calls on from the web). */
export interface DeviceConfirmation {
  id: string
  device_id: string
  change: { answer_calls?: boolean }
  status: 'pending' | string
  created_at: string
  expires_at?: string
}

/** The device view, with `confirmation` when the phone must confirm the change. */
export type DevicePatched = T.Device & { confirmation?: DeviceConfirmation | null }

/** The phone hasn't accepted turning calls on yet. */
export function callsPending(d: Pick<T.Device, 'answer_calls' | 'pending_confirmation'> | null | undefined): boolean {
  return !!d && !d.answer_calls && !!d.pending_confirmation?.change?.answer_calls
}

// ── Sim: linked standby and runs ────────────────────────────────────────────────────────────────────────────────
/** A session as `GET /v1/sessions/{id}` shows it; once a session that adopted a phone ends, `next_pairing_id` is the
 * standby pairing that keeps the phone linked (P1): pass it to the next `POST /v1/sessions`. */
export interface SimSessionView {
  id: string
  state: string
  task: { id: string; title: string; summary?: string; tips?: string[] }
  mission_id: string | null
  phone_connected: boolean
  phone_paired?: boolean
  practice: boolean
  next_pairing_id?: string | null
  [extra: string]: unknown
}

export interface SimSubmitResult {
  submission_id: string
  practice: boolean
  /** A linked session (it adopted a phone) ends at submit; others restart the scene. */
  session_ended?: boolean
  /** The standby pairing that keeps the phone linked; null when the phone wasn't connected at submit (start a fresh
   * call then). */
  next_pairing_id?: string | null
}

/** POST /v1/sessions/{id}/end. */
export interface SimEndResult {
  ok: boolean
  next_pairing_id?: string | null
}

/** GET /v1/me/submissions/{id}: one run, the owner's view. `status` is in the shared set (or a sim status that
 * `statusOf` maps). */
export interface SimRun {
  id: string
  task_id: string
  task_title: string
  area?: string | null
  created_at: string
  duration_s?: number | null
  mode_switches?: number | null
  /** The reward week the run counts for and when it is paid (the server's week; older servers omit it). */
  reward_week?: RewardWeekRef | null
  status: string
  practice: boolean
  practice_reason?: string | null
  points?: number | null
  /** Signed, owner only; usually null (no per-run video yet): show the task picture, no play button. */
  video_url?: string | null
  /** The stored episode, for the replay in the page (GET /v1/episodes/{id}/playback); null while its check runs. */
  episode_id?: string | null
  /** 'page': this run replays in the page (the run player); null: no replay yet. */
  replay?: 'page' | null
  mission_id?: string | null
  session_id?: string | null
  decided_at?: string | null
}

/** GET /v1/me/submissions (exists): a page of runs. The redesign fields are optional until the service ships them. */
export interface SimRunRow {
  id: string
  task: { id: string; title: string | null; key?: string | null }
  task_title?: string | null
  area?: string | null
  /** The sim's raw status (pending | validating | accepted | rejected). */
  status: string
  /** The same run in the shared set: checking | accepted | practice | not_accepted. */
  shared_status?: string | null
  /** The server's words for a status this build doesn't know. */
  status_label?: string | null
  points?: number | null
  practice: boolean
  practice_reason?: string | null
  mission_id?: string | null
  created_at: string
  decided_at?: string | null
  duration_s?: number | null
  validation?: { duration_s?: number | null } | null
}

/** The older single read (`GET /v1/submissions/{id}`), used when the owner read isn't deployed yet. */
interface LegacySubmission extends SimRunRow {
  validation?: { passed: boolean; duration_s: number | null } | null
}

function fromLegacy(r: LegacySubmission): SimRun {
  return {
    id: r.id,
    task_id: r.task.id,
    task_title: r.task_title ?? r.task.title ?? 'Robot task',
    area: r.area ?? null,
    created_at: r.created_at,
    duration_s: r.duration_s ?? null,
    mode_switches: null,
    status: r.shared_status ?? r.status,
    practice: r.practice,
    practice_reason: r.practice_reason ?? null,
    points: r.points ?? null,
    video_url: null,
    mission_id: r.mission_id ?? null,
  }
}

export function robotApi(c: ApiClient) {
  return {
    calls: {
      /** Web session only. 409 `no_phone` when no phone of the person's answers calls; 404 while `phone_call` is off. */
      create: (body: CallCreate) => c.post<CallCreated>('platform', '/v1/me/calls', body),
      /** Poll about once a second while the call card is up. */
      get: (id: string) => c.get<CallStatus>('platform', `/v1/me/calls/${enc(id)}`),
    },
    devices: {
      patch: (id: string, body: DevicePatch) => c.patch<DevicePatched>('platform', `/v1/me/devices/${enc(id)}`, body),
    },
    standby: {
      /** The browser ends the link (P1); the phone sees `link_ended`. */
      end: (pairingId: string) => c.delete<{ ok: boolean }>('sim', `/v1/pairings/${enc(pairingId)}`),
    },
    sessions: {
      get: (id: string) => c.get<SimSessionView>('sim', `/v1/sessions/${enc(id)}`),
      submit: (id: string) => c.post<SimSubmitResult>('sim', `/v1/sessions/${enc(id)}/submit`),
      end: (id: string) => c.post<SimEndResult>('sim', `/v1/sessions/${enc(id)}/end`),
    },
    runs: {
      list: (q: { status?: string; limit?: number } = {}) => c.get<T.List<SimRunRow>>('sim', '/v1/me/submissions', { query: q as never }),
      /** The owner's single-run read; falls back to the older read while the new one isn't deployed. */
      get: async (id: string): Promise<SimRun> => {
        try {
          return await c.get<SimRun>('sim', `/v1/me/submissions/${enc(id)}`)
        } catch (e) {
          if ((e as { status?: number }).status !== 404 && (e as { status?: number }).status !== 405) throw e
          return fromLegacy(await c.get<LegacySubmission>('sim', `/v1/submissions/${enc(id)}`))
        }
      },
    },
  }
}

export type RobotApi = ReturnType<typeof robotApi>
